// src/lib/paths.js

import { resolveCollectionPath, resolveDocPath, getSeasonID } from "../lib/seasonPaths";

const requireSeasonID = (override) => {
  if (override && typeof override === "string" && override.trim() !== "") {
    return override;
  }
  const s = getSeasonID();
  if (!s || typeof s !== "string") {
    throw new Error("paths: getSeasonID() returned an invalid season ID");
  }
  return s;
};

// ---- tiny guard to keep IDs tidy ----
const asStr = (v, name) => {
  if (typeof v !== "string" || v.trim() === "") {
    throw new Error(`paths: ${name} must be a non-empty string`);
  }
  return v;
};

// ---- root collections / docs ----
export const users = () => resolveCollectionPath(["users"]);
export const user = (uid) =>
  resolveDocPath(["users", asStr(uid, "uid")]);

export const leagues = () => resolveCollectionPath(["leagues"]);
export const league = (leagueID) =>
  resolveDocPath(["leagues", asStr(leagueID, "leagueID")]);

// ---- league subcollections (canonical names) ----
// All accept an optional seasonID as the last argument; falls back to global singleton.
export const leaguePlayers = (leagueID, seasonID) =>
  resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "players"]);
export const leagueTeams = (leagueID, seasonID) =>
  resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "teams"]);
export const leagueGameweeks = (leagueID, seasonID) =>
  resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "gameweeks"]);

// ---- gameweek doc + subcollections ----
export const gameweek = (leagueID, gwID, seasonID) =>
  resolveDocPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "gameweeks", asStr(gwID, "gwID")]);

export const gwMatches = (leagueID, gwID, seasonID) =>
  resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "gameweeks", asStr(gwID, "gwID"), "matches"]);
export const gwPlayerPoints = (leagueID, gwID, seasonID) =>
  resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "gameweeks", asStr(gwID, "gwID"), "playerPoints"]);
export const gwTeamPoints = (leagueID, gwID, seasonID) =>
  resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "gameweeks", asStr(gwID, "gwID"), "teamPoints"]);
export const gwTeams = (leagueID, gwID, seasonID) =>
  resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "gameweeks", asStr(gwID, "gwID"), "teams"]);

// ---- Back-compat aliases (so existing imports keep working) ----
export const players = (leagueID, seasonID) => resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "players"]);
export const teams = (leagueID, seasonID) => resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "teams"]);
export const gameweeksCol = (leagueID, seasonID) => resolveCollectionPath(["leagues", asStr(leagueID, "leagueID"), "seasons", requireSeasonID(seasonID), "gameweeks"]);

// ---- aggregated export (so `import { paths } ...` still works) ----
export const paths = {
  // roots
  users,
  user,
  leagues,
  league,

  // canonical
  leaguePlayers,
  leagueTeams,
  leagueGameweeks,
  gameweek,
  gwMatches,
  gwPlayerPoints,
  gwTeamPoints,
  gwTeams,

  // aliases (old names)
  players,
  teams,
  gameweeksCol,
};
